// ── Session meeting/provider status section (GATED-DARK, honest) ────────────
// Wave 6 (Sessions). The Zoom scheduling lane is GATED-DARK: a session record
// exists but the meeting provider is not created (dry-run). We render that
// HONESTLY — "Meeting not created (provider scheduling is closed)" — and NEVER a
// fake Zoom meeting/link/passcode. There is no live meeting object to read, so
// this is a status-only panel driven by the session's own fields + the dark-lane
// posture. A coach/admin can request a Zoom sync (the dark sync-zoom lane) but it
// returns provider_closed evidence, never a created meeting — handled by the
// owning page's action, not fabricated here.

import { Alert, Module, ModuleBody, ModuleHead, StatusDot } from "@vjal/ui";
import type { ReactNode } from "react";

import type { CoachingSessionDto } from "../../../services/coaching-dtos";
import { inviteStatusLabel, orDash } from "../sessions-helpers";

function MeetingField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <p className="m-0 text-sm text-ink-body">{children}</p>
    </div>
  );
}

export function SessionMeetingSection({ session }: { session: CoachingSessionDto }) {
  // The session's invite/meeting signal: when nothing has been requested (or the
  // provider is closed), we say so honestly. No join URL / passcode is ever shown.
  const inviteStatus = session.invite_status ?? "not_requested";
  const meetingDark = inviteStatus === "not_requested" || inviteStatus === "provider_closed";

  return (
    <Module span={6}>
      <ModuleHead title="Meeting" subtitle="Zoom / meeting-provider status for this session." />
      <ModuleBody className="space-y-3" data-honest-posture={meetingDark ? "not-configured" : undefined}>
        {meetingDark ? (
          <Alert variant="info">
            No meeting has been created yet. Zoom scheduling is not configured for this environment, so no join link or
            passcode is available. Session timing and invites are still tracked below.
          </Alert>
        ) : (
          <div className="flex items-center gap-2 text-sm text-ink-body">
            <StatusDot tone="info" label="Invite status" />
            <span>Invite status: {inviteStatusLabel(inviteStatus)}</span>
          </div>
        )}
        <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          <MeetingField label="Starts">{orDash(session.starts_at)}</MeetingField>
          <MeetingField label="Timezone">{orDash(session.timezone)}</MeetingField>
        </div>
      </ModuleBody>
    </Module>
  );
}
